/// <reference types="vite/client" />
import '@mantine/core/styles.css'
import '@mantine/spotlight/styles.css'
import '@mantine/charts/styles.css'
import '@mantine/dates/styles.css'
import '@mantine/dropzone/styles.css'
import '@mantine/notifications/styles.css'
import 'mantine-datatable/styles.css'
import '../globals.css'
import { useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react'
import { NuqsAdapter } from 'nuqs/adapters/tanstack-router'
import { isPublicRoute } from '@util'
import { theme, themeCssVariablesResolver } from '@theme'
import { HOUR, ID_PREFIXES, Logger, MINUTE, type TerrosHeaderMetadata } from '@terros/common'
import { createRootRoute, Outlet, useLocation, useNavigate } from '@tanstack/react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Notifications } from '@mantine/notifications'
import { ModalsProvider } from '@mantine/modals'
import { MantineProvider } from '@mantine/core'
import { useSocketInvalidation } from '@hooks'
import { HeadersContext, i18n, useProfile } from '@front/model'
import { IntercomProvider, LayoutWithoutAuth, NotReady } from '@components'
import { Auth0Provider, useAuth0, withAuthenticationRequired, type AppState } from '@auth0/auth0-react'
import { trackAnalytic, useAnalyticsPublisher } from '@analytics'
import { WebBackendProvider } from '../WebBackendProvider'
import auth0Configuration from '../auth0.json'

const logger = new Logger('RootLayout')

function AuthenticatedLayout({ children }: { children: React.ReactNode }): ReactNode {
  useSocketInvalidation()

  return (
    <IntercomProvider>
      <LayoutWithoutAuth>{children}</LayoutWithoutAuth>
    </IntercomProvider>
  )
}

const LayoutWithAuth = withAuthenticationRequired(AuthenticatedLayout)

function PublicLayout({ children }: { children: React.ReactNode }): ReactNode {
  return <div className='min-h-screen bg-gray-50'>{children}</div>
}

function AppContent(): ReactNode {
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated, isLoading } = useAuth0()
  const { isTantalim } = useProfile(undefined, isAuthenticated)

  useAnalyticsPublisher()

  useEffect(() => {
    if (isLoading) return

    const currentPath = location.pathname
    const isPublic = isPublicRoute(currentPath)
    const canViewPublic = !isAuthenticated || isTantalim

    if (!canViewPublic && isPublic) {
      logger.verbose(`${currentPath} is a public route, redirecting to /home`)
      navigate({ to: '/home' })
      return
    }

    if (!isAuthenticated && !isPublic) {
      logger.verbose(`${currentPath} is not a public route, redirecting to /`)
      navigate({ to: '/' })
      return
    }

    if (!isAuthenticated && !canViewPublic) {
      navigate({ to: '/login' })
    }
  }, [isAuthenticated, isLoading, location.pathname, navigate, isTantalim])

  useTrackRouteChange()

  if (isLoading) {
    return (
      <div className='flex min-h-screen items-center'>
        <NotReady isLoading />
      </div>
    )
  }

  const Layout = isPublicRoute(location.pathname) ? PublicLayout : LayoutWithAuth

  logger.verbose(location.pathname, isPublicRoute(location.pathname))

  return (
    <Layout>
      <Outlet />
    </Layout>
  )
}

const headers: TerrosHeaderMetadata = {
  'terros-session': crypto.randomUUID(),
  'terros-platform': 'web',
  'terros-development': import.meta.env.DEV ? 'true' : 'false',
}

function RootComponent(): ReactElement {
  const navigate = useNavigate()

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { staleTime: 20 * MINUTE, gcTime: HOUR },
        },
      })
  )

  const onRedirectCallback = (appState?: AppState): void => {
    // @ts-ignore
    navigate({ to: appState?.returnTo || '/home' })
  }

  return (
    <MantineProvider cssVariablesResolver={themeCssVariablesResolver} defaultColorScheme='auto' theme={theme}>
      <Notifications pr={70} />
      <NuqsAdapter>
        <QueryClientProvider client={queryClient}>
          <HeadersContext.Provider value={headers}>
            <Auth0Provider
              clientId={auth0Configuration.clientId}
              domain={auth0Configuration.domain}
              authorizationParams={{
                redirect_uri: window.location.origin,
              }}
              onRedirectCallback={onRedirectCallback}
            >
              <WebBackendProvider>
                <ModalsProvider>
                  <AppContent />
                </ModalsProvider>
              </WebBackendProvider>
            </Auth0Provider>
          </HeadersContext.Provider>
        </QueryClientProvider>
      </NuqsAdapter>
    </MantineProvider>
  )
}

export const Route = createRootRoute({
  component: RootComponent,
  errorComponent: ErrorComponent,
})

function ErrorComponent(): ReactElement {
  return (
    <div>
      <h1 className='text-center'>{i18n.t('Something went wrong. Check that you have cookies enabled.')}</h1>
    </div>
  )
}

function useTrackRouteChange(): void {
  const previousPathRef = useRef<string | undefined>(undefined)
  const location = useLocation()

  useEffect(() => {
    const previousPath = previousPathRef.current
    const currentPath = location.pathname
    const normalizedCurrentPath = normalizeRoutePath(currentPath)
    const normalizedPreviousPath = previousPath ? normalizeRoutePath(previousPath) : undefined

    if (previousPath !== currentPath) {
      logger.verbose(`Route changed from ${normalizedPreviousPath} to ${normalizedCurrentPath}`)
      trackAnalytic('RouteChange', {
        string: normalizedCurrentPath,
        metadata: {
          from: normalizedPreviousPath,
        },
      })
    }

    previousPathRef.current = currentPath
  }, [location.pathname])
}

function normalizeRoutePath(path: string): string {
  const pattern = new RegExp(`\\/(${ID_PREFIXES.join('|')})[.:_]([A-Za-z0-9_-]+)`, 'g')
  return path.replace(pattern, '/[id]')
}
